import { createFileRoute, Link } from "@tanstack/react-router";
import { hreflangLinks } from "@/lib/seo";
import { Layout } from "@/components/Layout";

const TITLE = "Oppimistorni — opas: turvallisuus, ikä & valinta keittiöön | Montessori24";
const DESCRIPTION =
  "Oppimistorni-opas suomeksi: mikä on oppimistorni (learning tower / taaperotorni), minkä ikäiselle se sopii, turvallisuusvaatimukset, oikea koko keittiöön ja materiaalivalinta. Asiantuntijan vinkit Montessori-pedagogiikan pohjalta.";
const URL = "https://montessori24.fi/oppimistorni-opas";

export const Route = createFileRoute("/oppimistorni-opas")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { name: "keywords", content: "oppimistorni, learning tower, montessori torni, keittiötorni lapselle, oppimistorni opas" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: URL },
      { property: "og:type", content: "article" },
    ],
    links: [{ rel: "canonical", href: URL }, ...hreflangLinks(URL)],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: "Oppimistorni-opas — näin valitset turvallisen ja kestävän tornin",
          description: DESCRIPTION,
          image: ["https://montessori24.fi/og-image.jpg"],
          datePublished: "2025-01-15",
          dateModified: "2026-07-01",
          author: { "@type": "Organization", name: "Montessori24" },
          publisher: {
            "@type": "Organization",
            name: "Montessori24",
            logo: { "@type": "ImageObject", url: "https://montessori24.fi/og-image.jpg" },
          },
          mainEntityOfPage: URL,
          inLanguage: "fi-FI",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "Minkä ikäiselle lapselle oppimistorni sopii?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Oppimistorni sopii lapselle, joka osaa seistä tukevasti itsenäisesti. Ikäsuositus vaihtelee mallin ja valmistajan mukaan — tarkista tuotekohtainen ikäsuositus tuotesivulta.",
              },
            },
            {
              "@type": "Question",
              name: "Onko oppimistorni turvallinen?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Hyvin suunniteltu oppimistorni on erittäin turvallinen: leveä jalusta, korkeat sivuseinät, liukumattomat alustat ja tukeva koivuvanerirakenne estävät kaatumisen. Aikuisen tulee aina valvoa käyttöä.",
              },
            },
            {
              "@type": "Question",
              name: "Mikä on oppimistorni ja miten se eroaa jakkarasta?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Oppimistorni on lapselle suunniteltu korotusteline, jossa on suojaavat sivuseinät ja takatuki. Toisin kuin jakkara, se mahdollistaa lapsen turvallisen työskentelyn aikuisen työtasolla pitkiäkin aikoja.",
              },
            },
            {
              "@type": "Question",
              name: "Mistä materiaalista hyvä oppimistorni on tehty?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Paras materiaali on koivuvaneri — kestävä, kevyt ja luonnollinen. Vältä halpoja muovi- tai MDF-malleja, jotka eivät kestä päivittäistä käyttöä ja voivat sisältää haitallisia kemikaaleja.",
              },
            },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Etusivu", item: "https://montessori24.fi/" },
            { "@type": "ListItem", position: 2, name: "Oppimistornit", item: "https://montessori24.fi/oppimistorni" },
            { "@type": "ListItem", position: 3, name: "Oppimistorni-opas", item: URL },
          ],
        }),
      },
    ],
  }),
  component: OppimistorniOpas,
});

function OppimistorniOpas() {
  return (
    <Layout>
      {/* Hero */}
      <section className="border-b border-border bg-cream">
        <div className="container-prose py-16 md:py-24">
          <div className="max-w-2xl">
            <span className="text-xs uppercase tracking-[0.25em] text-clay">Montessori-opas</span>
            <h1 className="mt-3 text-4xl text-foreground md:text-5xl text-balance">
              Oppimistorni — opas: näin valitset turvallisen taaperotornin keittiöön
            </h1>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground md:text-lg">
              Oppimistorni (englanniksi <em>learning tower</em>, suomeksi myös taaperotorni tai
              keittiöjakkara kaiteilla) on Montessori-pedagogiikan tunnetuin
              kaluste — ja syystä. Se tuo lapsen turvallisesti aikuisten työtasolle, jossa hän voi
              osallistua ruoanlaittoon, leipomiseen ja arjen askareisiin. Tässä oppaassa käymme läpi,
              mitä turvallisuusominaisuuksia kannattaa vaatia, mille iälle torni sopii ja miten valitset
              oikean mallin keittiösi mittoihin.
            </p>
          </div>
        </div>
      </section>

      {/* Sisällysluettelo */}
      <section className="border-b border-border bg-background">
        <div className="container-prose py-10">
          <h2 className="text-xs font-medium uppercase tracking-[0.2em] text-clay">Sisältö</h2>
          <ol className="mt-4 grid gap-2 text-sm text-muted-foreground md:grid-cols-2">
            <li><a href="#mika-on" className="underline underline-offset-4 hover:text-foreground">1. Mikä oppimistorni on?</a></li>
            <li><a href="#hyodyt" className="underline underline-offset-4 hover:text-foreground">2. Hyödyt lapsen kehitykselle</a></li>
            <li><a href="#ika" className="underline underline-offset-4 hover:text-foreground">3. Oikea ikä — milloin aloittaa?</a></li>
            <li><a href="#turvallisuus" className="underline underline-offset-4 hover:text-foreground">4. Turvallisuusominaisuudet</a></li>
            <li><a href="#koko" className="underline underline-offset-4 hover:text-foreground">5. Oikea koko keittiöön</a></li>
            <li><a href="#materiaali" className="underline underline-offset-4 hover:text-foreground">6. Materiaali ja kestävyys</a></li>
            <li><a href="#kaytto" className="underline underline-offset-4 hover:text-foreground">7. Vinkit käyttöön</a></li>
            <li><a href="#ukk" className="underline underline-offset-4 hover:text-foreground">8. Usein kysytyt kysymykset</a></li>
          </ol>
        </div>
      </section>

      {/* Sisältö */}
      <section className="bg-background">
        <div className="container-prose py-12 md:py-16">
          <article className="max-w-2xl space-y-12 text-[15px] leading-relaxed text-foreground md:text-base">
            <div id="mika-on">
              <span className="text-xs font-medium tracking-[0.2em] text-clay">01</span>
              <h2 className="mt-2 text-2xl text-foreground md:text-3xl">Mikä oppimistorni on?</h2>
              <p className="mt-4 text-muted-foreground">
                Oppimistorni on lapselle suunniteltu korotusteline, jossa on neljä tukijalkaa,
                korotettu seisomataso ja suojaavat sivuseinät. Toisin kuin tavallinen jakkara, torni
                estää lasta putoamasta sivuille tai taakse. Tämä mahdollistaa pitkät, keskittyneet
                työskentelyhetket aikuisen rinnalla — keittiössä, työpöydän ääressä tai pesualtaalla.
              </p>
              <p className="mt-3 text-muted-foreground">
                Oppimistorni perustuu Maria Montessorin havaintoon: lapsi oppii osallistumalla, ei
                katsomalla. Kun ympäristö on lapsen mittakaavassa, hän alkaa toimia itsenäisesti.
              </p>
            </div>

            <div id="hyodyt" className="border-t border-border pt-10">
              <span className="text-xs font-medium tracking-[0.2em] text-clay">02</span>
              <h2 className="mt-2 text-2xl text-foreground md:text-3xl">Hyödyt lapsen kehitykselle</h2>
              <ul className="mt-4 space-y-3 text-muted-foreground">
                <li><strong className="text-foreground">Itsenäisyys:</strong> Lapsi pääsee itse vesihanalle, leipomispöydälle ja työtasolle — ja kokee onnistumisen päivittäin.</li>
                <li><strong className="text-foreground">Hienomotoriikka:</strong> Kaataminen, sekoittaminen ja leikkaaminen kehittävät käden ja silmän yhteistyötä.</li>
                <li><strong className="text-foreground">Kielenkehitys:</strong> Yhdessä tekeminen synnyttää luontevia keskusteluja — keittiö on lapsen paras sanavarastokoulu.</li>
                <li><strong className="text-foreground">Keskittymiskyky:</strong> Turvallinen, vakaa asento antaa lapsen uppoutua tehtävään pitkäksi aikaa.</li>
                <li><strong className="text-foreground">Itsetunto:</strong> ”Minä osaan” -kokemus toistuu päivittäin, ja se rakentaa pohjaa elinikäiseen oppimiseen.</li>
              </ul>
            </div>

            <div id="ika" className="border-t border-border pt-10">
              <span className="text-xs font-medium tracking-[0.2em] text-clay">03</span>
              <h2 className="mt-2 text-2xl text-foreground md:text-3xl">Oikea ikä — milloin aloittaa?</h2>
              <p className="mt-4 text-muted-foreground">
                Oppimistornin voi ottaa käyttöön heti, kun lapsi seisoo itsenäisesti tukevasti —
                yleensä <strong className="text-foreground">12–18 kuukauden iässä</strong>. Alle
                vuoden ikäiselle torni ei ole vielä tarpeellinen, koska lapsi tarvitsee enemmän
                liikkumatilaa lattialla.
              </p>
              <p className="mt-3 text-muted-foreground">
                Useimmat lapset käyttävät tornia <strong className="text-foreground">5–6-vuotiaaksi</strong>
                asti. Säädettävä seisomataso pidentää käyttöikää, koska torni kasvaa lapsen mukana.
              </p>
            </div>

            <div id="turvallisuus" className="border-t border-border pt-10">
              <span className="text-xs font-medium tracking-[0.2em] text-clay">04</span>
              <h2 className="mt-2 text-2xl text-foreground md:text-3xl">Turvallisuusominaisuudet</h2>
              <p className="mt-4 text-muted-foreground">Hyvässä oppimistornissa on aina:</p>
              <ul className="mt-3 space-y-3 text-muted-foreground">
                <li><strong className="text-foreground">Leveä jalusta:</strong> Vähintään 40 cm joka suuntaan estää kaatumisen, kun lapsi nojautuu sivulle.</li>
                <li><strong className="text-foreground">Korkeat sivuseinät:</strong> Ulottuvat seisomatason yläpuolelle ja tuovat lapselle lisätukea. Aikuisen valvonta on silti aina tarpeen.</li>
                <li><strong className="text-foreground">Pyöristetyt kulmat:</strong> Ei teräviä reunoja, jotka voivat satuttaa.</li>
                <li><strong className="text-foreground">Liukumaton seisomataso:</strong> Uritettu tai pinnoitettu pinta pitää jalat paikallaan myös märkänä.</li>
                <li><strong className="text-foreground">Tukeva rakenne:</strong> Ei keinu eikä natise, vaikka lapsi liikkuu reippaasti.</li>
                <li><strong className="text-foreground">Turvalliset pinnoitteet:</strong> EN 71-3 -vaatimusten mukaiset maalit ja lakat — tarkista tuotekohtaiset tiedot.</li>
              </ul>
              <p className="mt-3 text-muted-foreground">
                Muista: oppimistorni ei korvaa aikuisen valvontaa. Pysy aina lähellä, kun lapsi on tornissa.
              </p>
            </div>

            <div id="koko" className="border-t border-border pt-10">
              <span className="text-xs font-medium tracking-[0.2em] text-clay">05</span>
              <h2 className="mt-2 text-2xl text-foreground md:text-3xl">Oikea koko keittiöön</h2>
              <p className="mt-4 text-muted-foreground">
                Mittaa keittiösi työtason korkeus — Suomessa yleisin on <strong className="text-foreground">90 cm</strong>.
                Lapsen seisomataso pitää saada noin <strong className="text-foreground">15–20 cm alle työtason</strong>,
                jotta lapsen kyynärpäät ovat sopivalla korkeudella.
              </p>
              <p className="mt-3 text-muted-foreground">
                Yleiset mitat:
              </p>
              <ul className="mt-3 space-y-2 text-muted-foreground">
                <li>Tornin korkeus: 85–95 cm</li>
                <li>Seisomatason koko: 35 × 40 cm (tilaa molemmille jaloille ja liikkumiseen)</li>
                <li>Säädettävä taso: 3–4 korkeutta (30 / 40 / 50 cm lattiasta)</li>
                <li>Paino: 7–10 kg (tarpeeksi tukeva, mutta siirreltävissä)</li>
              </ul>
            </div>

            <div id="materiaali" className="border-t border-border pt-10">
              <span className="text-xs font-medium tracking-[0.2em] text-clay">06</span>
              <h2 className="mt-2 text-2xl text-foreground md:text-3xl">Materiaali ja kestävyys</h2>
              <p className="mt-4 text-muted-foreground">
                Paras materiaali on <strong className="text-foreground">koivuvaneri</strong> — kestävä,
                kevyt ja luonnollinen. Hyvälaatuinen koivuvanerinen torni kestää sukupolvelta toiselle
                ja kuluessaankin näyttää kauniilta. Vältä:
              </p>
              <ul className="mt-3 space-y-2 text-muted-foreground">
                <li>Halvat <strong className="text-foreground">MDF-mallit</strong> — turpoavat kosteudessa.</li>
                <li><strong className="text-foreground">Muovirakenteet</strong> — eivät kestä päivittäistä käyttöä, voivat sisältää ftalaatteja.</li>
                <li>Käsittelemättömät pinnat — likaantuvat keittiössä nopeasti.</li>
              </ul>
              <p className="mt-3 text-muted-foreground">
                Etsi tornit, joissa on vesipohjaiset, turvalliset lakat ja FSC-sertifioitu puu.
              </p>
            </div>

            <div id="kaytto" className="border-t border-border pt-10">
              <span className="text-xs font-medium tracking-[0.2em] text-clay">07</span>
              <h2 className="mt-2 text-2xl text-foreground md:text-3xl">Vinkit käyttöön</h2>
              <ul className="mt-4 space-y-3 text-muted-foreground">
                <li><strong className="text-foreground">Aloita pienestä:</strong> Anna lapsen ensin tutustua torniin matalalla työllä — esim. vihannesten pesulla.</li>
                <li><strong className="text-foreground">Anna oikeat työvälineet:</strong> Pieni veitsi, lasinen kannu, oikea kaulin. Lapsi tuntee kunnioituksen, kun saa oikeita työkaluja.</li>
                <li><strong className="text-foreground">Hyväksy sotku:</strong> Maito kaatuu, jauho lentää. Sotku on osa oppimista — pyyhe käden ulottuvilla.</li>
                <li><strong className="text-foreground">Pidä rutiini:</strong> Sama hetki päivässä (esim. aamupala) tekee tornista lapsen ”oman tilan”.</li>
                <li><strong className="text-foreground">Säilytä lähellä:</strong> Jos torni on aina näkyvillä, lapsi pyytää itse kiivetä — itsenäisyys kasvaa luontevasti.</li>
              </ul>
            </div>

            <div id="ukk" className="border-t border-border pt-10">
              <span className="text-xs font-medium tracking-[0.2em] text-clay">08</span>
              <h2 className="mt-2 text-2xl text-foreground md:text-3xl">Usein kysytyt kysymykset</h2>
              <dl className="mt-4 space-y-5">
                <div>
                  <dt className="font-medium text-foreground">Minkä ikäiselle oppimistorni sopii?</dt>
                  <dd className="mt-1 text-muted-foreground">Heti kun lapsi seisoo itsenäisesti tukevasti — yleensä 12–18 kk. Käyttöikä jatkuu 5–6 vuoteen asti.</dd>
                </div>
                <div>
                  <dt className="font-medium text-foreground">Onko oppimistorni turvallinen?</dt>
                  <dd className="mt-1 text-muted-foreground">Kyllä — kun valitset mallin, jossa on leveä jalusta, korkeat sivuseinät ja tukeva koivuvanerirakenne. Aikuisen valvonta on aina suositeltavaa.</dd>
                </div>
                <div>
                  <dt className="font-medium text-foreground">Mikä on hyvän tornin hintahaarukka?</dt>
                  <dd className="mt-1 text-muted-foreground">Laadukas, kestävä koivuvanerinen oppimistorni maksaa yleensä 150–350 €. Halvemmat mallit eivät usein täytä turvallisuusvaatimuksia.</dd>
                </div>
                <div>
                  <dt className="font-medium text-foreground">Voiko tornia käyttää muualla kuin keittiössä?</dt>
                  <dd className="mt-1 text-muted-foreground">Ehdottomasti. Torni toimii pesualtaalla hampaiden pesussa, työpöydän ääressä piirtämisessä ja jopa parvekkeella istuttamassa kukkia.</dd>
                </div>
              </dl>
            </div>
          </article>

          {/* CTA */}
          <div className="mt-16 flex flex-col items-start gap-4 border-t border-border pt-10 md:flex-row md:items-center md:justify-between">
            <p className="max-w-lg text-sm text-muted-foreground md:text-base">
              Valmis tuomaan oppimistornin kotiin? Selaa Montessori24:n koivuvanerisia malleja.
            </p>
            <Link
              to="/kauppa"
              search={{ kategoria: "tower" }}
              className="inline-flex items-center rounded-full bg-primary px-7 py-3.5 text-sm font-medium text-primary-foreground transition-transform hover:-translate-y-0.5"
            >
              Katso oppimistornit →
            </Link>
          </div>

          {/* Suositut mallit — direct jump to product pages */}
          <div className="mt-10">
            <span className="text-[11px] font-medium uppercase tracking-[0.22em] text-clay">Suositut mallit</span>
            <div className="mt-3 flex flex-wrap gap-2">
              <Link
                to="/product/$handle"
                params={{ handle: "montessori-helper-tower-step-stool-kitchen-tower-with-blackboard" }}
                className="rounded-full bg-foreground px-4 py-2 text-sm text-background hover:opacity-90"
              >
                Oppimistorni liitutaululla →
              </Link>
              <Link
                to="/product/$handle"
                params={{ handle: "montessori-helper-tower-step-stool" }}
                className="rounded-full bg-foreground px-4 py-2 text-sm text-background hover:opacity-90"
              >
                Oppimistorni apujakkara →
              </Link>
              <Link
                to="/product/$handle"
                params={{ handle: "2in1-kitchen-tower-table-chair" }}
                className="rounded-full bg-foreground px-4 py-2 text-sm text-background hover:opacity-90"
              >
                2-in-1 oppimistorni + pöytä →
              </Link>
              <Link
                to="/product/$handle"
                params={{ handle: "transformable-kitchen-tower-table-chair" }}
                className="rounded-full border border-border px-4 py-2 text-sm text-foreground hover:border-foreground"
              >
                Transformable Tower →
              </Link>
              <Link
                to="/oppimistorni"
                className="rounded-full border border-border px-4 py-2 text-sm text-muted-foreground hover:border-foreground hover:text-foreground"
              >
                Kaikki oppimistornit
              </Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}